'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  dictionaries,
  isLocale,
  localeFromTelegram,
  LEGACY_LOCALE_STORAGE_KEY,
  LOCALE_META,
  LOCALE_STORAGE_KEY,
  translate,
  type Locale,
  type Messages,
} from '../i18n'
import { useTelegramBotAPI } from '../hooks/useTelegramBotAPI'

type TranslateFn = (key: string, vars?: Record<string, string | number | boolean | null | undefined>) => string

type LocaleContextValue = {
  locale: Locale
  dir: 'rtl' | 'ltr'
  messages: Messages
  setLocale: (locale: Locale) => void
  t: TranslateFn
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

function readStoredLocale(): Locale | null {
  try {
    const raw =
      localStorage.getItem(LOCALE_STORAGE_KEY) ||
      localStorage.getItem(LEGACY_LOCALE_STORAGE_KEY)
    if (!isLocale(raw)) return null
    if (localStorage.getItem(LOCALE_STORAGE_KEY) !== raw) {
      localStorage.setItem(LOCALE_STORAGE_KEY, raw)
      localStorage.removeItem(LEGACY_LOCALE_STORAGE_KEY)
    }
    return raw
  } catch {
    return null
  }
}

function applyDocumentLocale(locale: Locale) {
  const meta = LOCALE_META[locale]
  const root = document.documentElement
  root.lang = locale
  root.dir = meta.dir
  root.dataset.locale = locale
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const { user: tgUser } = useTelegramBotAPI()
  const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale() || 'fa')

  useEffect(() => {
    const stored = readStoredLocale()
    if (stored) {
      setLocaleState(stored)
      return
    }
    const fromTg = localeFromTelegram(tgUser?.language_code)
    if (fromTg) setLocaleState(fromTg)
  }, [tgUser?.language_code])

  useEffect(() => {
    applyDocumentLocale(locale)
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale)
    } catch {
      /* ignore */
    }
  }, [locale])

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next)
  }, [])

  const messages = dictionaries[locale]

  const t = useCallback<TranslateFn>(
    (key, vars) => translate(messages, key, vars),
    [messages],
  )

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      dir: LOCALE_META[locale].dir,
      messages,
      setLocale,
      t,
    }),
    [locale, messages, setLocale, t],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale() {
  const ctx = useContext(LocaleContext)
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider')
  return ctx
}

export function useT() {
  return useLocale().t
}
